import { configureAmplify, fetchAuthSession, useAuthenticator } from '@parkchirp/api-client';
import { CommonContext } from 'common-ui-library';
import React, {
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { AccessControlComponent } from '~/components/AccessControl';
import { AccessControlledComponents, amplifyConfig } from '~/constants';
import { Organization, SnackBarProps } from '~/types';

import AppLayout from '../layouts/appLayout';

configureAmplify(amplifyConfig, false);

interface Details {
	description?: string | null;
	info?: string | null;
	leftSideInfo?: React.ReactElement;
}

export interface AppContextInterface {
	initialized: boolean;
	setInitialized: Dispatch<SetStateAction<boolean>>;
	userToken: string | null;
	setUserToken: Dispatch<SetStateAction<string | null>>;
	userRole: string | null;
	setUserRole: Dispatch<SetStateAction<string | null>>;
	invoiceNotes: string;
	setInvoiceNotes: Dispatch<SetStateAction<string | null>>;
	organizationList: Organization[];
	organizationName: string | null;
	setOrganizationName: Dispatch<SetStateAction<string | null>>;
	organizationId: number | null;
	dashboardAccessConfig: string[];
	setOrganizationId: Dispatch<SetStateAction<number | null>>;
	paymentTypes: string[];
	setPaymentTypes: Dispatch<SetStateAction<string[]>>;
	organization: Organization | null;
	setOrganization: Dispatch<SetStateAction<Organization | null>>;
	showDialog: ReactNode | null;
	setShowDialog: Dispatch<SetStateAction<ReactNode | null>>;
	showSnackbar: Partial<SnackBarProps> | null;
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackBarProps> | null>>;
	headerDetails: Details | null;
	setHeaderDetails: Dispatch<SetStateAction<Details | null>>;
	setOrganizationList: Dispatch<SetStateAction<Organization[]>>;
	setDashboardAccessConfig: Dispatch<SetStateAction<string[]>>;
	hasComponentAccess: (componentName: AccessControlledComponents) => boolean;
	dashboardAccessConfigLoading: boolean;
	setDashboardAccessConfigLoading: Dispatch<SetStateAction<boolean>>;
}

const defaultAppContext = {
	initialized: false,
	setInitialized: () => {},
	userToken: null,
	setUserToken: () => {},
	userRole: null,
	setUserRole: () => {},
	invoiceNotes: '',
	setInvoiceNotes: () => {},
	organizationName: null,
	setOrganizationName: () => {},
	organizationList: [],
	setOrganizationList: () => {},
	organizationId: null,
	setOrganizationId: () => {},
	dashboardAccessConfig: [],
	setDashboardAccessConfig: () => {},
	paymentTypes: [],
	setPaymentTypes: () => {},
	organization: null,
	setOrganization: () => {},
	showDialog: null,
	setShowDialog: () => {},
	showSnackbar: null,
	setShowSnackbar: () => {},
	headerDetails: null,
	setHeaderDetails: () => {},
	hasComponentAccess: () => false,
	dashboardAccessConfigLoading: true,
	setDashboardAccessConfigLoading: () => true,
} as AppContextInterface;

export const AppContext = createContext(defaultAppContext);

// These roles are not allowed to access the admin dashboard
// For these roles we redirect back to the main site
const redirectRoles = ['subscriber', 'opsl0'];

const AppContextProvider = ({ Component, pageProps }) => {
	const [initialized, setInitialized] = useState<boolean>(false);
	const [invoiceNotes, setInvoiceNotes] = useState<string | null>(null);
	const [organization, setOrganization] = useState<Organization | null>(null);
	const [organizationList, setOrganizationList] = useState<Organization[]>([]);
	const [organizationName, setOrganizationName] = useState<string | null>(null);
	const [organizationId, setOrganizationId] = useState<number | null>(null);
	const [paymentTypes, setPaymentTypes] = useState<string[]>([]);
	const [userToken, setUserToken] = useState<string | null>(null);
	const [userRole, setUserRole] = useState<string | null>(null);
	const [showDialog, setShowDialog] = useState<ReactNode | null>(null);
	const [showSnackbar, setShowSnackbar] = useState<Partial<SnackBarProps> | null>(null);
	const [headerDetails, setHeaderDetails] = useState<Details | null>(null);
	const [dashboardAccessConfig, setDashboardAccessConfig] = useState<string[]>([]);
	const [dashboardAccessConfigLoading, setDashboardAccessConfigLoading] = useState<boolean>(true);

	const { user } = useAuthenticator((context) => [context.user]);

	useEffect(() => {
		const fetchUserData = async () => {
			try {
				const { idToken } = (await fetchAuthSession()).tokens ?? {};

				const metadata = JSON.parse(idToken.payload.metadata as string);

				const { role, organization: userOrganizationId } = metadata;

				if (redirectRoles.includes(role)) {
					window.location.href = process.env.NEXT_PUBLIC_MONTHLY_URL;
				}

				setUserToken(idToken.toString());
				setUserRole(role);
				setOrganizationId(userOrganizationId ? Number(userOrganizationId) : null);
			} catch (error) {
				console.error('Error fetching user data:', error);
			}
		};

		if (!userToken) {
			fetchUserData();
		}
	}, [user, userToken, organizationList, setOrganizationList]);

	useEffect(() => {
		if (!window.location.pathname.includes('detail')) {
			setHeaderDetails(null);
		}
	}, [window.location.pathname]);

	const hasComponentAccess = useCallback(
		(componentName: AccessControlledComponents) =>
			dashboardAccessConfig && dashboardAccessConfig.includes(componentName),
		[dashboardAccessConfig],
	);

	const values = useMemo(
		() => ({
			initialized,
			setInitialized,
			invoiceNotes,
			setInvoiceNotes,
			userToken,
			setUserToken,
			userRole,
			setUserRole,
			organization,
			setOrganization,
			organizationList,
			setOrganizationList,
			organizationName,
			setOrganizationName,
			organizationId,
			setOrganizationId,
			paymentTypes,
			setPaymentTypes,
			showDialog,
			setShowDialog,
			showSnackbar,
			setShowSnackbar,
			headerDetails,
			setHeaderDetails,
			dashboardAccessConfig,
			setDashboardAccessConfig,
			hasComponentAccess,
			dashboardAccessConfigLoading,
			setDashboardAccessConfigLoading,
		}),
		[
			initialized,
			invoiceNotes,
			userToken,
			userRole,
			organization,
			organizationList,
			organizationName,
			organizationId,
			paymentTypes,
			showDialog,
			showSnackbar,
			headerDetails,
			dashboardAccessConfig,
			hasComponentAccess,
			dashboardAccessConfigLoading,
		],
	);

	const commonValues = useMemo(
		() => ({
			AccessControlComponent,
			AccessControlledComponents,
			hasComponentAccess,
			setShowDialog,
			setShowSnackbar,
			organizationId,
		}),
		[hasComponentAccess, setShowDialog, setShowSnackbar, organizationId],
	);

	return (
		<AppContext.Provider value={values}>
			<CommonContext.Provider value={commonValues}>
				<AppLayout>
					<Component {...pageProps} />
				</AppLayout>
			</CommonContext.Provider>
		</AppContext.Provider>
	);
};

export default AppContextProvider;
